import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { TurnstileAction } from '@kaeliux/shared';

interface TurnstileApi {
    render(element: HTMLElement, options: Record<string, unknown>): string;
    execute(widgetId: string): void;
    reset(widgetId: string): void;
    remove(widgetId: string): void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 120_000;

let scriptLoading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    scriptLoading ??= new Promise<TurnstileApi>((resolve, reject) => {
        const fail = () => {
            scriptLoading = null;
            reject(new Error("The bot check couldn't load. Check your connection and try again."));
        };
        const script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        script.onload = () => (window.turnstile ? resolve(window.turnstile) : fail());
        script.onerror = fail;
        document.head.appendChild(script);
    });
    return scriptLoading;
}

export interface TurnstileCheck {
    /** Where the widget mounts. It stays invisible unless Cloudflare needs the player to interact. */
    containerRef: RefObject<HTMLDivElement | null>;
    /** A fresh, single-use token for this widget's action. Overlapping calls share one token request. */
    getToken: () => Promise<string>;
}

interface PendingToken {
    promise: Promise<string>;
    resolve: (token: string) => void;
    reject: (error: Error) => void;
}

/**
 * Cloudflare Turnstile bot check for one action (guest sign-up or guest-ID
 * sign-in). The server verifies the token was issued for that same action.
 * Mock mode aliases this hook to `hooks/mock/useTurnstile`.
 */
export function useTurnstile(action: TurnstileAction): TurnstileCheck {
    const containerRef = useRef<HTMLDivElement>(null);
    const widget = useRef<{ id: string; container: HTMLElement } | null>(null);
    const pending = useRef<PendingToken | null>(null);

    useEffect(() => () => {
        pending.current?.reject(new Error('The bot check was interrupted. Please try again.'));
        if (widget.current) window.turnstile?.remove(widget.current.id);
        widget.current = null;
    }, []);

    const getToken = useCallback(async () => {
        if (pending.current) return pending.current.promise;
        const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
        if (!siteKey) throw new Error('Guest play is not available right now.');
        const turnstile = await loadTurnstile();
        if (pending.current) return (pending.current as PendingToken).promise;
        const container = containerRef.current;
        if (!container) throw new Error('The bot check is not ready yet. Please try again.');

        const request = {} as PendingToken;
        const timer = setTimeout(() => request.reject(new Error('The bot check took too long. Please try again.')), TOKEN_TIMEOUT_MS);
        request.promise = new Promise<string>((resolve, reject) => {
            const settle = () => {
                clearTimeout(timer);
                if (pending.current === request) pending.current = null;
            };
            request.resolve = (token) => { settle(); resolve(token); };
            request.reject = (error) => { settle(); reject(error); };
        });
        pending.current = request;

        // The page can move the container between layouts; a widget left in a detached element never answers.
        if (widget.current && widget.current.container !== container) {
            turnstile.remove(widget.current.id);
            widget.current = null;
        }
        if (!widget.current) {
            const timedOut = () => pending.current?.reject(new Error('The bot check timed out. Please try again.'));
            const id = turnstile.render(container, {
                sitekey: siteKey,
                action,
                execution: 'execute',
                appearance: 'interaction-only',
                callback: (token: string) => pending.current?.resolve(token),
                'error-callback': () => pending.current?.reject(new Error('The bot check failed. Please try again.')),
                'timeout-callback': timedOut,
                'expired-callback': timedOut,
            });
            widget.current = { id, container };
        } else {
            turnstile.reset(widget.current.id);
        }
        turnstile.execute(widget.current.id);
        return request.promise;
    }, [action]);

    return { containerRef, getToken };
}
